import Box from "@mui/material/Box";

import { SHIPPING_METHOD_TWO_DAY } from "~/shared/sharedConstants";
import { kickBlue, kickDarkGrayBlue } from "~/shared/styles/baseStyles";
import { formatPhoneNumber } from "~/shared/utils/sharedUtils";
import {
  formatDeliveryDates,
  formatShippingDate,
} from "./useCheckoutShippingEstimates";

export default function CheckoutNextSteps({
  isPickup,
  isRefill,
  pharmacyInfo,
  shipmentMethod,
  estimate,
  needsShippingState,
}) {
  const deliveryDates = isPickup ? "" : formatDeliveryDates(estimate);
  const shipByDate = deliveryDates
    ? formatShippingDate(estimate.shipByDate)
    : "";
  const carrier =
    shipmentMethod === SHIPPING_METHOD_TWO_DAY
      ? "FedEx Priority"
      : "USPS Standard";
  const steps = [];

  if (!isRefill) {
    steps.push({
      key: "review",
      title: "A doctor reviews your visit",
      timing: "Usually within hours · 7 days a week",
    });
  }
  if (isPickup) {
    const pharmacyName = pharmacyInfo?.pharmacyName?.trim() || "your pharmacy";
    const phoneDigits = String(pharmacyInfo?.pharmacyPhone || "")
      .replace(/\D/g, "")
      .replace(/^1(\d{10})$/, "$1");
    const pharmacyPhone = formatPhoneNumber(phoneDigits);
    steps.push(
      {
        key: "prescription",
        title: `We send your prescription to ${pharmacyName}`,
        timing: "Within minutes of approval",
      },
      {
        key: "pickup",
        title: "Ready for pickup",
        timing: "Usually the same day",
        description: (
          <>
            Call{" "}
            {pharmacyPhone ? (
              <a
                href={`tel:+1${phoneDigits}`}
                style={{
                  color: "inherit",
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                }}
              >
                {pharmacyPhone}
              </a>
            ) : (
              "your pharmacy"
            )}{" "}
            to confirm before you go. You pay the pharmacy for the medication.
          </>
        ),
      },
    );
  } else {
    steps.push(
      {
        key: "shipping",
        title: `Ships via ${carrier}`,
        timing: shipByDate,
      },
      {
        key: "arrival",
        title: "Estimated arrival",
        timing: deliveryDates,
      },
    );
  }

  let dateNote = "";
  if (!isPickup && needsShippingState) {
    dateNote = "Select your state to see estimated delivery dates.";
  } else if (deliveryDates && !isRefill) {
    dateNote = "Dates assume doctor approval today.";
  }

  return (
    <Box
      component="section"
      aria-label="What happens after this"
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        backgroundColor: "#FAF9FC",
        border: "1px solid #E7E3EF",
        borderRadius: "10px",
        padding: "16px",
        marginBottom: "24px",
        color: kickDarkGrayBlue,
      }}
    >
      <Box
        component="h2"
        sx={{
          color: kickBlue,
          fontFamily:
            '"BasisGrotesquePro-Bold", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: "17px",
          lineHeight: 1.4,
          margin: "0 0 16px",
        }}
      >
        What happens after this
      </Box>
      <Box
        component="ol"
        role="list"
        sx={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          fontSize: "14px",
          lineHeight: 1.5,
          display: "flex",
          flexDirection: "column",
          "& li + li": { marginTop: "12px" },
        }}
      >
        {steps.map(({ key, title, timing, description }, index) => (
          <Box
            component="li"
            key={key}
            sx={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "20px minmax(0, 1fr)",
              columnGap: "10px",
              "&:not(:last-child)::after": {
                content: '""',
                position: "absolute",
                left: "9.5px",
                top: "24px",
                bottom: "-8px",
                width: "1px",
                backgroundColor: "#E7E3EF",
              },
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                width: "20px",
                height: "20px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #D8D3E5",
                borderRadius: "50%",
                backgroundColor: "#fff",
                color: kickBlue,
                fontSize: "12px",
                fontWeight: 700,
              }}
            >
              {index + 1}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  fontFamily:
                    '"BasisGrotesquePro-Bold", "Helvetica", "Arial", sans-serif',
                  fontWeight: 700,
                }}
              >
                {title}
              </Box>
              {timing && (
                <Box
                  sx={{
                    marginTop: "2px",
                    color:
                      key === "arrival" || key === "pickup" ? kickBlue : "#666",
                    fontSize:
                      key === "arrival" || key === "shipping" ? "15px" : "13px",
                    fontWeight:
                      key === "arrival" || key === "pickup" ? 700 : 400,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.45,
                  }}
                >
                  {key === "arrival"
                    ? timing
                        .split(" – ")
                        .map((date) => date.replace(/ /g, "\u00a0"))
                        .join(" – ")
                    : timing}
                </Box>
              )}
              {description && (
                <Box component="p" sx={{ margin: "3px 0 0", fontSize: "13px" }}>
                  {description}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      {dateNote && (
        <Box
          component="p"
          sx={{
            fontSize: "13px",
            lineHeight: 1.5,
            color: "#666",
            margin: "16px 0 0",
            paddingTop: "12px",
            borderTop: "1px solid #E7E3EF",
          }}
        >
          {dateNote}
        </Box>
      )}
    </Box>
  );
}
